import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Tooltip,
  Chip,
  FormControlLabel,
  Switch,
  Menu,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  FilterList as FilterIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import type { AgGridReact } from 'ag-grid-react';
import { GridRefreshControls } from './GridRefreshControls';

export interface GridRibbonControl {
  id: string;
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  variant?: 'button' | 'chip' | 'switch';
  value?: boolean;
}

interface AgGridRibbonProps<TData = any> {
  gridRef: React.RefObject<AgGridReact<TData>>;
  gridDataKey: string;
  controls?: GridRibbonControl[];
  onSearch?: (searchTerm: string) => void;
  showSelectedFilter?: boolean;
  onFullscreenToggle?: () => void;
  isFullscreen?: boolean;
  totalCount?: number;
  onRefresh?: () => void;
  refreshing?: boolean;
  autoRefreshIntervalMs?: number;
}

export const AgGridRibbon = <TData,>({
  gridRef,
  gridDataKey,
  controls = [],
  onSearch,
  showSelectedFilter = true,
  onFullscreenToggle,
  isFullscreen = false,
  totalCount: propTotalCount,
  onRefresh,
  refreshing = false,
  autoRefreshIntervalMs,
}: AgGridRibbonProps<TData>) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showOnlySelected, setShowOnlySelected] = useState(false);
  const [selectedCount, setSelectedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [settingsAnchor, setSettingsAnchor] = useState<null | HTMLElement>(null);

  // Search persistence key
  const searchKey = `gridsearch-${gridDataKey}`;

  // Load saved search term
  useEffect(() => {
    const savedSearch = localStorage.getItem(searchKey);
    if (savedSearch) {
      setSearchTerm(savedSearch);
      if (onSearch) {
        onSearch(savedSearch);
      }
    }
  }, [searchKey, onSearch]);

  // Update counts from grid
  useEffect(() => {
    const updateCounts = () => {
      const gridApi = gridRef.current?.api;
      if (!gridApi) return;

      setSelectedCount(gridApi.getSelectedRows().length);
      // Use prop totalCount if provided (for server-side row model), otherwise get from grid
      if (propTotalCount !== undefined) {
        setTotalCount(propTotalCount);
      } else {
        setTotalCount(gridApi.getDisplayedRowCount());
      }
    };

    const gridApi = gridRef.current?.api;
    if (gridApi) {
      updateCounts();
      
      const events = ['selectionChanged', 'filterChanged', 'rowDataUpdated'];
      events.forEach(event => {
        gridApi.addEventListener(event as any, updateCounts);
      });

      return () => {
        events.forEach(event => {
          gridApi.removeEventListener(event as any, updateCounts);
        });
      };
    }
  }, [gridRef, propTotalCount]);

  // Handle search
  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);
    
    // Save to localStorage
    if (value) {
      localStorage.setItem(searchKey, value);
    } else {
      localStorage.removeItem(searchKey);
    }

    // Apply quick filter if no custom search handler
    if (!onSearch) {
      gridRef.current?.api.setGridOption('quickFilterText', value);
    } else {
      onSearch(value);
    }
  }, [gridRef, searchKey, onSearch]);

  // Clear search
  const handleClearSearch = useCallback(() => {
    setSearchTerm('');
    localStorage.removeItem(searchKey);
    
    if (!onSearch) {
      gridRef.current?.api.setGridOption('quickFilterText', '');
    } else {
      onSearch('');
    }
  }, [gridRef, searchKey, onSearch]);

  // Toggle fullscreen
  const handleFullscreenToggle = useCallback(() => {
    if (onFullscreenToggle) {
      onFullscreenToggle();
    }
  }, [onFullscreenToggle]);


  // Toggle selected filter
  const handleSelectedFilterToggle = useCallback(() => {
    const newValue = !showOnlySelected;
    setShowOnlySelected(newValue);
    
    const gridApi = gridRef.current?.api;
    if (!gridApi) return;

    if (newValue) {
      gridApi.onFilterChanged();
    } else {
      gridApi.onFilterChanged();
    }
  }, [showOnlySelected, gridRef]);

  // Settings menu handlers
  const handleSettingsClick = (event: React.MouseEvent<HTMLElement>) => {
    setSettingsAnchor(event.currentTarget);
  };

  const handleSettingsClose = () => {
    setSettingsAnchor(null);
  };

  // Export to CSV
  const handleExportCsv = () => {
    gridRef.current?.api.exportDataAsCsv({
      fileName: `${gridDataKey}-export.csv`,
    });
    handleSettingsClose();
  };

  return (
    <Box
      className="ag-grid-ribbon"
      sx={{
        bgcolor: 'background.default',
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      {/* Search */}
      <Box className="ag-grid-ribbon-search">
        <TextField
          size="small"
          placeholder="Search..."
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />,
            endAdornment: searchTerm && (
              <IconButton size="small" onClick={handleClearSearch}>
                <ClearIcon />
              </IconButton>
            ),
          }}
          sx={{ width: '100%' }}
        />
      </Box>

      {/* Row counts */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Chip
          label={`${totalCount} rows`}
          size="small"
          variant="outlined"
        />
        {selectedCount > 0 && (
          <Chip
            label={`${selectedCount} selected`}
            size="small"
            color="primary"
          />
        )}
      </Box>

      {/* Selected filter toggle */}
      {showSelectedFilter && selectedCount > 0 && (
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={showOnlySelected}
              onChange={handleSelectedFilterToggle}
            />
          }
          label="Show selected only"
        />
      )}

      {/* Default controls */}
      <Box className="ag-grid-ribbon-controls">

        {onRefresh && (
          <GridRefreshControls
            onRefresh={onRefresh}
            refreshing={refreshing}
            intervalMs={autoRefreshIntervalMs}
          />
        )}

        <Tooltip title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}>
          <IconButton size="small" onClick={handleFullscreenToggle}>
            {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
        </Tooltip>

        <Tooltip title="Settings">
          <IconButton size="small" onClick={handleSettingsClick}>
            <SettingsIcon />
          </IconButton>
        </Tooltip>

        {/* Settings menu */}
        <Menu
          anchorEl={settingsAnchor}
          open={Boolean(settingsAnchor)}
          onClose={handleSettingsClose}
        >
          <MenuItem onClick={handleExportCsv}>
            Export to CSV
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => {
            gridRef.current?.api.sizeColumnsToFit();
            handleSettingsClose();
          }}>
            Size columns to fit
          </MenuItem>
          <MenuItem onClick={() => {
            const gridApi = gridRef.current?.api;
            if (gridApi) {
              const allColumnIds = gridApi.getColumns()?.map(column => column.getColId()) || [];
              gridApi.autoSizeColumns(allColumnIds);
            }
            handleSettingsClose();
          }}>
            Auto-size all columns
          </MenuItem>
        </Menu>
      </Box>

      {/* Custom controls */}
      {controls.length > 0 && (
        <>
          <Divider orientation="vertical" flexItem />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {controls.map((control) => {
              if (control.variant === 'chip') {
                return (
                  <Chip
                    key={control.id}
                    label={control.label}
                    onClick={control.onClick}
                    disabled={control.disabled}
                    size="small"
                    color="primary"
                    variant="filled"
                    icon={control.icon as React.ReactElement}
                  />
                );
              }

              if (control.variant === 'switch') {
                return (
                  <FormControlLabel
                    key={control.id}
                    control={
                      <Switch
                        size="small"
                        checked={control.value || false}
                        onChange={control.onClick}
                        disabled={control.disabled}
                      />
                    }
                    label={control.label}
                  />
                );
              }

              return (
                <Tooltip key={control.id} title={control.label}>
                  <IconButton
                    size="small"
                    onClick={control.onClick}
                    disabled={control.disabled}
                  >
                    {control.icon || <FilterIcon />}
                  </IconButton>
                </Tooltip>
              );
            })}
          </Box>
        </>
      )}
    </Box>
  );
};

export default AgGridRibbon;